import { useState, useEffect, useRef } from 'react';
import { Globe, Search, Sparkles, Check, Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import type { ResearchStatus } from '../../types/settings';

interface ResearchProgressViewProps {
  currentStatus: ResearchStatus;
  error?: string | null;
  onRetry: () => void;
  onSkip: () => void;
}

interface StepConfig {
  key: ResearchStatus;
  icon: React.ElementType;
  label: string;
  messages: string[];
}

const STEPS: StepConfig[] = [
  {
    key: 'scraping_website',
    icon: Globe,
    label: 'Analyzing your online presence',
    messages: [
      'Reading your homepage...',
      'Checking your LinkedIn page...',
      'Exploring your product pages...',
      'Extracting key information...',
      'Analyzing your company profile...',
      'Understanding your value proposition...',
    ],
  },
  {
    key: 'researching',
    icon: Search,
    label: 'Researching your market',
    messages: [
      'Identifying competitors in your space...',
      'Studying the competitive landscape...',
      'Analyzing market positioning...',
      'Researching industry trends...',
    ],
  },
  {
    key: 'generating_insights',
    icon: Sparkles,
    label: 'Generating insights',
    messages: [
      'Mapping prospect pain points...',
      'Crafting solution narratives...',
      'Generating benefits analysis...',
      'Putting it all together...',
    ],
  },
];

const STEP_ORDER: ResearchStatus[] = ['scraping_website', 'researching', 'generating_insights'];

function getStepIndex(status: ResearchStatus): number {
  return STEP_ORDER.indexOf(status);
}

function formatElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  return `${seconds}s`;
}

export function ResearchProgressView({ currentStatus, error, onRetry, onSkip }: ResearchProgressViewProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [, setTick] = useState(0);
  const [stepStartTimes, setStepStartTimes] = useState<Record<string, number>>({});
  const globalStartRef = useRef(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setMessageIndex(prev => prev + 1), 3500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentStatus && STEP_ORDER.includes(currentStatus) && !stepStartTimes[currentStatus]) {
      setStepStartTimes(prev => ({ ...prev, [currentStatus]: Date.now() }));
    }
  }, [currentStatus]);

  if (currentStatus === 'failed') {
    return (
      <div className="py-10">
        <div className="max-w-md mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-red-50 mb-4">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h3 className="text-lg font-extrabold text-gray-900 font-display mb-2">Research Failed</h3>
          <p className="text-sm text-gray-500 mb-6">{error || 'An unexpected error occurred.'}</p>
          <div className="flex flex-col gap-3 items-center">
            <button
              onClick={onRetry}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg transition-all"
            >
              <RotateCcw className="w-4 h-4" />
              Try Again
            </button>
            <button onClick={onSkip} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
              Fill manually instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentStatus === 'completed') {
    const totalElapsed = Date.now() - globalStartRef.current;
    return (
      <div className="py-10">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/20 mb-4">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center">
              <Check className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-lg font-extrabold text-gray-900 font-display">Research Complete!</h3>
          <p className="text-sm text-gray-500 mt-1">Completed in {formatElapsed(totalElapsed)} -- Loading your results...</p>
        </div>
      </div>
    );
  }

  const activeStepIndex = getStepIndex(currentStatus);
  const safeIndex = activeStepIndex >= 0 ? activeStepIndex : 0;
  const activeStep = STEPS[safeIndex];
  const currentMessage = activeStep.messages[messageIndex % activeStep.messages.length];
  const totalElapsed = Date.now() - globalStartRef.current;

  return (
    <div className="py-8">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <h3 className="text-lg font-extrabold text-gray-900 font-display mb-1">Researching your company</h3>
          <p className="text-sm text-gray-500">Our AI is analyzing your website and building your sales profile.</p>
        </div>

        <div className="space-y-1 mb-8">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isCompleted = safeIndex > index;
            const isActive = safeIndex === index;
            const isPending = safeIndex < index;

            const stepStart = stepStartTimes[step.key];
            const nextStepKey = STEP_ORDER[index + 1];
            const nextStepStart = nextStepKey ? stepStartTimes[nextStepKey] : null;

            let elapsedText = '';
            if (isCompleted && stepStart) {
              const endTime = nextStepStart || Date.now();
              elapsedText = `Done in ${formatElapsed(endTime - stepStart)}`;
            } else if (isActive && stepStart) {
              elapsedText = formatElapsed(Date.now() - stepStart);
            }

            return (
              <div key={step.key} className="flex items-center gap-4 py-3">
                <div className={`relative w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-500 ${
                  isCompleted
                    ? 'bg-gradient-to-br from-[#1875FF] to-[#05E0D1]'
                    : isActive
                      ? 'bg-gradient-to-br from-[#1875FF]/15 to-[#05E0D1]/15 ring-2 ring-[#1875FF]/30'
                      : 'bg-gray-100'
                }`}>
                  {isCompleted ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : isActive ? (
                    <Loader2 className="w-5 h-5 text-[#1875FF] animate-spin" />
                  ) : (
                    <Icon className="w-5 h-5 text-gray-400" />
                  )}
                  {isActive && (
                    <div className="absolute inset-0 rounded-xl bg-[#1875FF]/10 animate-ping" style={{ animationDuration: '2s' }} />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isCompleted ? 'text-gray-900' : isActive ? 'text-[#1875FF]' : 'text-gray-400'
                  }`}>
                    {step.label}
                  </p>
                  {isActive && (
                    <p className="text-xs text-gray-400 mt-0.5">{currentMessage}</p>
                  )}
                  {isCompleted && elapsedText && (
                    <p className="text-xs text-[#05E0D1] mt-0.5">{elapsedText}</p>
                  )}
                </div>

                <div className="shrink-0">
                  {isActive && elapsedText && (
                    <span className="text-xs font-mono text-[#1875FF]/60">{elapsedText}</span>
                  )}
                  {isPending && (
                    <div className="w-2 h-2 rounded-full bg-gray-200" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-gray-400">
          Total: {formatElapsed(totalElapsed)} -- Usually takes 100-150 seconds
        </p>
      </div>
    </div>
  );
}
